import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Mail, Phone, MapPin, MessageCircle, ArrowRight, Instagram, Linkedin, Youtube, Facebook } from "lucide-react";
import { CONTACT, SERVICES } from "@/data/site";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const [form, setForm] = useState({
    name: "", email: "", phone: "", company: "", service: "", message: "",
  });
  const [loading, setLoading] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.service || !form.message) {
      toast.error("Please fill in all required fields.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Thanks! We'll get back to you within 24 hours.");
      setForm({ name: "", email: "", phone: "", company: "", service: "", message: "" });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const isEmailErr = Array.isArray(detail) && detail.some((d) => (d?.loc || []).includes("email"));
      if (isEmailErr) toast.error("Please enter a valid email address.");
      else toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="relative py-24 md:py-40 bg-brand-brown text-white border-t border-brand-border overflow-hidden">
      <div aria-hidden className="absolute -top-40 right-0 w-[600px] h-[600px] rounded-full bg-brand-brown/20 blur-3xl" />
      <div aria-hidden className="absolute -bottom-40 -left-40 w-[500px] h-[500px] rounded-full bg-brand-gold/10 blur-3xl" />

      <div className="relative max-w-[1600px] mx-auto px-6 md:px-12 lg:px-24">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-20">
          {/* Left column */}
          <div className="lg:col-span-5">
            <p className="eyebrow text-brand-gold mb-6">(09) — Let's talk</p>
            <h2 className="font-display text-5xl sm:text-6xl lg:text-7xl leading-[1] tracking-tight font-medium">
              Start something <span className="italic text-brand-gold">worth signing</span>.
            </h2>
            <p className="mt-8 text-white/70 text-lg leading-relaxed max-w-md">
              Tell us about your brand and the goal you&apos;re chasing. We&apos;ll respond within 24 hours with a plan and a call invite.
            </p>

            <div className="mt-12 space-y-6">
              <a href={`mailto:${CONTACT.email}`} data-testid="contact-email" className="flex items-center gap-4 group">
                <span className="w-12 h-12 rounded-full border border-white/20 flex items-center justify-center group-hover:bg-brand-gold group-hover:text-brand-charcoal group-hover:border-brand-gold transition">
                  <Mail size={16} />
                </span>
                <div>
                  <p className="text-xs uppercase tracking-[0.24em] text-white/50">Email</p>
                  <p className="font-medium">{CONTACT.email}</p>
                </div>
              </a>
              <a href={`tel:+${CONTACT.phoneRaw}`} data-testid="contact-phone" className="flex items-center gap-4 group">
                <span className="w-12 h-12 rounded-full border border-white/20 flex items-center justify-center group-hover:bg-brand-gold group-hover:text-brand-charcoal group-hover:border-brand-gold transition">
                  <Phone size={16} />
                </span>
                <div>
                  <p className="text-xs uppercase tracking-[0.24em] text-white/50">Phone</p>
                  <p className="font-medium">{CONTACT.phone}</p>
                </div>
              </a>
              <div className="flex items-center gap-4">
                <span className="w-12 h-12 rounded-full border border-white/20 flex items-center justify-center">
                  <MapPin size={16} />
                </span>
                <div>
                  <p className="text-xs uppercase tracking-[0.24em] text-white/50">Studio</p>
                  <p className="font-medium">{CONTACT.address}</p>
                </div>
              </div>
            </div>

            <div className="mt-12 flex flex-wrap items-center gap-3">
              <a
                data-testid="whatsapp-cta"
                href={CONTACT.whatsapp}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-brand-gold text-brand-charcoal font-semibold text-sm hover:bg-brand-beige transition"
              >
                <MessageCircle size={16} />
                Chat on WhatsApp
              </a>
              <div className="flex items-center gap-2 ml-2">
                {[
                  { Icon: Instagram, url: CONTACT.socials.instagram, label: "instagram" },
                  { Icon: Linkedin, url: CONTACT.socials.linkedin, label: "linkedin" },
                  { Icon: Youtube, url: CONTACT.socials.youtube, label: "youtube" },
                  { Icon: Facebook, url: CONTACT.socials.facebook, label: "facebook" },
                ].map(({ Icon, url, label }) => (
                  <a
                    key={label}
                    data-testid={`social-${label}`}
                    href={url}
                    target="_blank"
                    rel="noreferrer"
                    className="w-10 h-10 rounded-full border border-white/20 flex items-center justify-center hover:bg-brand-gold hover:text-brand-chocolate hover:border-brand-gold transition"
                    aria-label={label}
                  >
                    <Icon size={15} />
                  </a>
                ))}
              </div>
            </div>

            {/* Map placeholder */}
            <div className="mt-10 aspect-[16/9] overflow-hidden border border-white/10">
              <iframe
                title="DigiDoot Studio location"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d31108.75!2d77.59456!3d12.9716!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3bae1670c9b44e6d%3A0xf8dfc3e8517e4fe0!2sBengaluru!5e0!3m2!1sen!2sin!4v0000000000"
                className="w-full h-full grayscale opacity-80"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </div>

          {/* Right column: form */}
          <motion.form
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.9, ease: [0.19, 1, 0.22, 1] }}
            onSubmit={submit}
            data-testid="contact-form"
            className="lg:col-span-7 bg-white/[0.03] backdrop-blur-xl border border-white/10 p-8 md:p-12"
          >
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <Label className="text-xs uppercase tracking-[0.24em] text-white/60">Name*</Label>
                <Input
                  data-testid="contact-name"
                  value={form.name}
                  onChange={(e) => update("name", e.target.value)}
                  className="mt-2 bg-transparent border-0 border-b border-white/20 rounded-none px-0 focus-visible:ring-0 focus-visible:border-brand-gold text-white placeholder:text-white/30"
                  placeholder="Jane Doe"
                />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-[0.24em] text-white/60">Email*</Label>
                <Input
                  data-testid="contact-email-input"
                  type="email"
                  value={form.email}
                  onChange={(e) => update("email", e.target.value)}
                  className="mt-2 bg-transparent border-0 border-b border-white/20 rounded-none px-0 focus-visible:ring-0 focus-visible:border-brand-gold text-white placeholder:text-white/30"
                  placeholder="you@brand.com"
                />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-[0.24em] text-white/60">Phone*</Label>
                <Input
                  data-testid="contact-phone-input"
                  value={form.phone}
                  onChange={(e) => update("phone", e.target.value)}
                  className="mt-2 bg-transparent border-0 border-b border-white/20 rounded-none px-0 focus-visible:ring-0 focus-visible:border-brand-gold text-white placeholder:text-white/30"
                  placeholder="+91 98XXXXXXXX"
                />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-[0.24em] text-white/60">Company</Label>
                <Input
                  data-testid="contact-company"
                  value={form.company}
                  onChange={(e) => update("company", e.target.value)}
                  className="mt-2 bg-transparent border-0 border-b border-white/20 rounded-none px-0 focus-visible:ring-0 focus-visible:border-brand-gold text-white placeholder:text-white/30"
                  placeholder="Your brand"
                />
              </div>
              <div className="md:col-span-2">
                <Label className="text-xs uppercase tracking-[0.24em] text-white/60">Service required*</Label>
                <Select value={form.service} onValueChange={(v) => update("service", v)}>
                  <SelectTrigger
                    data-testid="contact-service"
                    className="mt-2 bg-transparent border-0 border-b border-white/20 rounded-none px-0 focus:ring-0 text-white h-11"
                  >
                    <SelectValue placeholder="Choose a service" />
                  </SelectTrigger>
                  <SelectContent>
                    {SERVICES.map((s) => (
                      <SelectItem key={s.title} value={s.title}>{s.title}</SelectItem>
                    ))}
                    <SelectItem value="Not sure yet">Not sure yet</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="md:col-span-2">
                <Label className="text-xs uppercase tracking-[0.24em] text-white/60">Message*</Label>
                <Textarea
                  data-testid="contact-message"
                  rows={4}
                  value={form.message}
                  onChange={(e) => update("message", e.target.value)}
                  className="mt-2 bg-transparent border-0 border-b border-white/20 rounded-none px-0 focus-visible:ring-0 focus-visible:border-brand-gold text-white placeholder:text-white/30 resize-none"
                  placeholder="Tell us about your brand and what you&apos;d like to achieve..."
                />
              </div>
            </div>

            <button
              data-testid="contact-submit"
              type="submit"
              disabled={loading}
              className="mt-10 group inline-flex items-center gap-3 px-8 py-4 rounded-full bg-brand-gold text-brand-charcoal font-semibold text-sm hover:bg-brand-beige transition-colors disabled:opacity-70"
            >
              {loading ? "Sending..." : "Send message"}
              <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
            </button>
          </motion.form>
        </div>
      </div>
    </section>
  );
}
